/**
 * Numbers the whole game agrees on. Anything a designer might want to turn
 * lives here rather than being spelled out at a call site, so there is one
 * place to look when behavior needs to change.
 */

/**
 * Server tick rate. DESIGN.md 5: this is not a twitch game, the tension is
 * informational, so the netcode budget goes to correctness rather than
 * latency.
 */
export const TICK_HZ = 15;
export const TICK_SECONDS = 1 / TICK_HZ;

/**
 * The client renders between the last two simulated states rather than at the
 * newest one, so movement is smooth at any display refresh rate.
 */
export const INTERPOLATION_MS = 100;

/**
 * Camera projection. DESIGN.md 5: angled orthographic, no vanishing point,
 * because perspective implies a viewer standing somewhere and this is the view
 * of a system rather than a person.
 *
 * TILT_DEG is measured away from straight down, so 0 is a plan view and 90
 * would be a horizon-level view. Ground distances running north to south
 * foreshorten by cos(tilt); height rises on screen by sin(tilt).
 */
export const TILT_DEG = 55;
export const YAW_DEG = 0;

/** Meters per mile, which the section-line road grid is built on. */
export const METERS_PER_MILE = 1609.344;

/**
 * Free-flow speed in meters per second by road class. Real posted speeds, not
 * tuned for feel. Congestion is not modeled yet.
 */
export const ROAD_CLASS_SPEED: Readonly<Record<string, number>> = Object.freeze({
  motorway: 31.3, // 70 mph
  // Ramps, and the reason they are separate.
  //
  // These were folded into the road they serve, so an on-ramp ran at 70 mph and
  // getting onto a motorway cost nothing at all. With 212 places to join at no
  // cost, the router made every cross-county trip a freeway trip -- including
  // Muskego to Hartland, which drove 2.8 km *east* to reach an on-ramp before
  // heading west, and 7 km further overall to save three minutes.
  //
  // A ramp is a slow curve you decelerate onto and accelerate off. Charging for
  // it is what makes a short freeway hop not worth the detour, which is the
  // real reason people do not take the interstate to the next town.
  motorway_link: 15.6, // 35 mph
  trunk: 29.1, // 65 mph
  trunk_link: 13.4, // 30 mph
  primary: 24.6, // 55 mph
  primary_link: 11.2, // 25 mph
  secondary: 22.4, // 50 mph
  secondary_link: 11.2,
  tertiary: 20.1, // 45 mph
  tertiary_link: 11.2,
  unclassified: 17.9, // 40 mph, the paved-but-minor rural road
  residential: 11.2, // 25 mph
  service: 6.7, // 15 mph
  track: 6.7,
});

/** Drawing width in meters by road class, used by the renderer. */
export const ROAD_CLASS_WIDTH: Readonly<Record<string, number>> = Object.freeze({
  motorway: 22,
  motorway_link: 9,
  trunk_link: 8,
  primary_link: 7,
  secondary_link: 7,
  tertiary_link: 6,
  trunk: 18,
  primary: 15,
  secondary: 13,
  tertiary: 11,
  unclassified: 9,
  residential: 8,
  service: 6,
  track: 5,
});

/**
 * How much room a car leaves in front of the one ahead, at rest.
 *
 * Two meters, so a stopped queue sits a bit over a car length apart rather than
 * bumper to bumper -- which is what happens, and is also what makes a queue
 * legible as a queue on a map rather than as one long smear.
 */
export const FOLLOWING_METERS = 2;

/**
 * How wide a lane is, in meters.
 *
 * The US standard, and it is the number the map is drawn against: two cars
 * abreast on a four-lane arterial have to look like two cars abreast rather
 * than one car drawn twice.
 */
export const LANE_WIDTH = 3.5;

/**
 * How far ahead a driver looks for a reason to change lanes.
 *
 * Short enough that a car does not pull out for something a quarter mile away,
 * long enough that it commits before it is already braking.
 */
export const PASS_LOOK_METERS = 90;

/**
 * How slow the car in front has to be before anybody goes round it.
 *
 * A fraction of the posted limit. Nobody overtakes a car that is doing the
 * limit, and this is the rule that keeps the county from turning into a
 * demolition derby of cars leapfrogging each other at 55 in a 55: on a road
 * where everybody is doing what they should, nothing here ever fires.
 */
export const PASS_SLOWER_THAN = 0.85;

/**
 * How much clear road an overtaker needs on the other side.
 *
 * A quarter of a mile. This is the number that decides whether an overtake on
 * a two-lane road is a maneuver or a head-on, and it is deliberately
 * generous: the simulation cannot see round a bend the way a driver can, so it
 * has to be more careful than a person would be rather than less.
 */
export const PASS_SIGHT_METERS = 400;

/**
 * How much over the limit an overtaker goes.
 *
 * Because you cannot pass somebody at the same speed they are doing. This is
 * also the thing that makes it an offence: the car is over the line *and* over
 * the limit, which is two of them, and a marked unit that happens to be within
 * PASS_WATCH_METERS is going to see both.
 */
export const PASS_SPEED = 1.25;

/**
 * How long an overtake may last before the driver gives up and pulls back in.
 *
 * A safety valve rather than a rule of the road. Without it a car that started
 * to pass and then could not finish would sit in the oncoming lane for the
 * rest of its trip.
 */
export const PASS_SECONDS = 12;

/**
 * How close a marked unit has to be to see it.
 *
 * Five hundred feet. Not a detection radius in the plate-reader sense and not
 * meant to be one: this is an officer with eyes, on the same stretch of road,
 * watching a car cross the line at 25% over. It is the manual counterpart of
 * everything the network does automatically, and the difference is the whole
 * argument -- one of these needs somebody to be there.
 */
export const PASS_WATCH_METERS = 152.4;

/**
 * What a marked unit is out on, in the words a scanner would use.
 *
 * Deliberately coarse and deliberately *not* an explanation. A dispatch code is
 * what somebody with a scanner hears, and it is a category rather than an
 * account: "suspicious vehicle" is a real thing to be sent to and tells a
 * listener nothing about whether anybody did anything.
 *
 * `hotlist` is the one that matters for the argument. It is the only call in
 * this list that begins with a machine, and a civilian hearing it is hearing
 * the far end of a plate read that happened to somebody -- which is otherwise
 * completely invisible from outside.
 */
export const CALL_KINDS = [
  'hotlist',
  'traffic',
  'speed',
  'alarm',
  'welfare',
  'crash',
  'assist',
  'suspicious',
  'stolen',
] as const;
export type CallKind = (typeof CALL_KINDS)[number];

export const CALL_WORDS: Readonly<Record<CallKind, string>> = Object.freeze({
  hotlist: 'vehicle of interest',
  traffic: 'traffic stop',
  speed: 'speed enforcement',
  alarm: 'alarm call',
  welfare: 'welfare check',
  crash: 'collision',
  assist: 'assisting another unit',
  suspicious: 'suspicious vehicle',
  // The manual half of finding a stolen car: knocking on doors, checking the
  // lots people leave them in, driving the roads out of town. It is in this
  // list rather than in the hotlist one because it is what an officer does
  // when no camera has seen the plate -- which, for most of the time a car is
  // missing, is the situation.
  stolen: 'investigating stolen vehicle leads',
});

/**
 * How far past the posted limit a driver can put their foot.
 *
 * A multiplier on the limit, and the reason it exists is that a civilian could
 * not speed at all: the throttle was clamped to 1, so the fastest anybody could
 * ever go was exactly what the sign said. That makes half the game unplayable.
 * The point of a plate reader county is that ordinary driving generates a
 * record; the point of *choosing to speed* is that it is a decision with a cost,
 * and there was no decision available.
 *
 * 1.4 on top of a driver's own habit tops out near 65% over. In a 25 that is
 * about 40, in a 55 about 90 -- fast enough to matter and to be worth it, and
 * conspicuous enough that it should not be free.
 */
export const PLAYER_MAX_THROTTLE = 1.4;

/**
 * How far over the limit somebody has to be before an officer does anything.
 *
 * Twenty percent. Simulated traffic runs to SPEED_MULTIPLIER_MAX of 1.18, so
 * ordinary drivers doing five over sit just inside this and nothing happens to
 * them -- which is both correct and the reason the number is where it is. A
 * threshold below the ambient speed of the county would mean every car on the
 * road being pulled over forever.
 *
 * This is what a person sees, not what a camera records, and the difference is
 * the argument. Being noticed for it takes an officer within
 * PASS_WATCH_METERS at the moment you do it. Nobody there, nothing happens.
 */
export const SPEEDING_NOTICED = 1.2;

/**
 * A plate you picked yourself, and why the game lets you.
 *
 * DESIGN.md section 4 says the plate is the identity. It meant that about the
 * network -- every read, every search, every alert is keyed on this string and
 * nothing else -- but it is also true about the player, and that is the part
 * that was not being used.
 *
 * The problem it solves: a player who loses their browser has to name their
 * account, and the only handle the game has is the plate. A plate a machine
 * generated is seven characters nobody can hold in their head, so asking for
 * it from memory is asking for something nobody has. A plate the player picked
 * is a plate the player knows. Same field, same primary key, no second
 * identity invented beside it.
 *
 * It is also a real decision rather than a convenience. A memorable plate is
 * memorable to the officers hunting you as well as to you: an operator who saw
 * DIRTSTR go past once will recognize it again, where RJW-7923 is one of two
 * thousand strings that look alike. Choosing one trades a little of your
 * anonymity for being able to get back into your own car, which is the exact
 * trade the whole game is about.
 *
 * Wisconsin issues personalized plates up to seven characters. So does this.
 */
export const PLATE_CHARS_MIN = 2;
export const PLATE_CHARS_MAX = 7;
/** Letters, digits, and the one space or dash a real plate is allowed. */
export const PLATE_PATTERN = /^[A-Z0-9]+(?:[ -][A-Z0-9]+)?$/;

/**
 * A code for putting your keys on a second device.
 *
 * Six characters, ten minutes, one use. Read off the screen of a phone that is
 * already signed in and typed into the one that is not, so getting a second
 * device onto an account needs nothing remembered at all -- the code is in
 * front of you and the PIN is the only thing you were ever asked to know.
 *
 * Short-lived on purpose. It is a full handover of a keyring to whoever holds
 * it and the PIN, so it should stop being useful about as fast as somebody can
 * walk across a room with it.
 */
export const PAIRING_CHARS = 6;
export const PAIRING_SECONDS = 600;
/** No O/0 or I/1: this gets read off one screen and typed into another. */
export const PAIRING_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';

/**
 * How close you have to be to take somebody else's car.
 *
 * Close enough that you are standing at it. There is no lock-picking minigame
 * and there should not be: the interesting part of stealing a car in this game
 * is not the theft, it is the hour afterwards when the plate is still clean and
 * every camera you drive past is recording a car nobody has reported yet.
 */
export const STEAL_METERS = 20;

/**
 * How long before somebody notices their car is gone, in hours.
 *
 * Half an hour to two. Nobody watches their car leave -- they come out to it,
 * or they do not come out to it until morning, and the gap between those two is
 * the whole mechanic. A plate that is not on any list yet drives past readers
 * that record it anyway, and when the report finally lands, the trail was
 * already there waiting to be searched.
 *
 * Which is the strongest argument *for* the network in the game, and it is here
 * deliberately. A record that only ever showed the network being wrong would be
 * a worse argument than the true one.
 */
export const STOLEN_REPORT_HOURS_MIN = 0.5;
export const STOLEN_REPORT_HOURS_MAX = 2;

/**
 * How far a scanner picks a call up.
 *
 * Further than a siren carries, because it is a radio rather than a noise, and
 * nothing like county-wide: this is a receiver in a car, and the point of it is
 * that you have to be around for it.
 */
export const SCANNER_METERS = 4000;

/** What the app costs. About twelve ordinary jobs. */
export const SCANNER_PRICE = 700;

/**
 * A radar gun for a patrol car, bought out of the department's revenue.
 *
 * Without one an officer has no way to CLOCK a speed -- the number they were
 * reading before was data from the ether. With one, a car it is pointed at
 * reads its speed, and a stop of a car it clocked over the limit counts as a
 * stop with a reason rather than a wrongful one.
 */
export const RADAR_PRICE = 900;

/** How far a radar gun can read a plate's speed, in meters. */
export const RADAR_METERS = 250;

/**
 * What it costs a department's operational budget to send a unit on a call-out.
 * Cases closed refill the budget; standing sets how many units can be out at
 * once; rank sets who may make the call. Money funds the operation, never buys
 * the standing.
 */
export const DISPATCH_COST = 120;

/**
 * The garage economy. A garage holds one car to start; another slot is bought,
 * and you cannot own more cars than your garage holds. It also costs rent, billed
 * a game-week at a time out of the account -- a car is not free to keep, and an
 * account that lets the rent run dry hears about it.
 */
export const GARAGE_UPGRADE_PRICE = 1500;
export const GARAGE_RENT = 120;
export const GARAGE_RENT_PERIOD_DAYS = 7;

/** Nominal vehicle footprint in meters. */
export const VEHICLE_LENGTH = 4.6;
export const VEHICLE_WIDTH = 1.9;

/**
 * Spread of driver speed around the free-flow figure, as a multiplier. Some
 * people speed, some people do not, and the difference is what makes a
 * predicted arrival time a bet rather than arithmetic.
 */
export const SPEED_MULTIPLIER_MIN = 0.82;
export const SPEED_MULTIPLIER_MAX = 1.18;

/** Seconds a vehicle waits at its destination before picking a new one. */
export const DWELL_SECONDS_MIN = 12;
export const DWELL_SECONDS_MAX = 110;

/**
 * How long a day is, in world seconds. Real, by default.
 *
 * This is not a stylistic choice, it is forced. Trip length is physical: an
 * errand takes the eight minutes it takes, and no amount of scheduling changes
 * that. So the share of a county on the road at any moment is
 * (driving per day) / (length of a day), and a driver who spends forty minutes
 * a day driving is only realistically sparse on the roads if a day is a day.
 *
 * Shortening it to make patterns show up faster was tried and it inflates
 * traffic by the same factor: at a one hour day, half the county is permanently
 * in motion and the night never goes quiet. That costs two things the game
 * needs, which are somewhere to hide in busy hours and nowhere to hide at four
 * in the morning.
 *
 * DESIGN.md section 4 wants a persistent world where the table accumulates for
 * weeks anyway, so a real day is the right default and the wait is the point.
 * Development overrides it; see DEV_DAY_SECONDS.
 */
export const DAY_SECONDS = 86400;

/**
 * A day for working on the game, where a session has to show a routine
 * repeating rather than a single morning.
 *
 * Set through HOTLIST_DAY_SECONDS. Everything it changes is the *schedule*:
 * cars still drive at posted speeds over real distances. What it costs is
 * honesty about density, which is a fine trade while nobody is playing.
 */
export const DEV_DAY_SECONDS = 7200;

/**
 * How many places a driver goes to regularly, beyond home.
 *
 * DESIGN.md section 8: the pattern is habit, not employment. A plate reader
 * network reconstructs a life out of the boring parts of it, and the boring
 * parts are a handful of places on a rhythm.
 */
export const HABITS_MIN = 2;
export const HABITS_MAX = 5;

/**
 * How far a habitual destination can be from home.
 *
 * People shop near where they live. Scattering somebody's regular places across
 * a whole county would make their week look like noise, and it is precisely
 * because a real activity space is small and repetitive that it is legible.
 */
export const HABIT_RADIUS_METERS = 12000;

/** Chance that a driver with nothing scheduled goes somewhere anyway. */
export const UNPLANNED_TRIP_CHANCE = 0.55;

/**
 * How long a read stays in the table.
 *
 * Thirty days, and it is not a design choice. CALIBRATION.md, from the
 * 2026-08-17 harvest of Wisconsin transparency portals: **every one of the 38
 * agencies reporting a retention period reports 30 days.** Not a median with a
 * spread around it. Thirty, everywhere, unanimously.
 *
 * So it is a hard edge rather than a tunable, and it cuts both ways, which is
 * why it is worth building rather than ignoring:
 *
 * - It is the exact width of the reconstruction window. Enforcement cannot
 *   answer a question about day 31 no matter how good the query is.
 * - It is a defense the other side can plan around, and the only one in the
 *   game that requires no skill at all. Waiting works.
 *
 * The audit log deliberately does **not** expire on this schedule. A search
 * outlives the data it was run against, which is the sharpest thing the record
 * has to say about itself.
 */
export const RETENTION_DAYS = 30;

/**
 * A world timestamp, as a console would print it.
 *
 * Shared so the query log and the audit log cannot drift apart, and taking the
 * day length rather than assuming one, because a county can run a compressed
 * day and every timestamp in the interface has to agree with it.
 */
export function formatClock(seconds: number, daySeconds: number = DAY_SECONDS): string {
  const total = Math.floor(seconds);
  const day = Math.max(1, Math.floor(daySeconds));
  const days = Math.floor(total / day);
  const rest = total % day;
  const h = String(Math.floor(rest / 3600)).padStart(2, '0');
  const m = String(Math.floor((rest % 3600) / 60)).padStart(2, '0');
  const s = String(rest % 60).padStart(2, '0');
  return `d${days} ${h}:${m}:${s}`;
}

/**
 * How close you have to be to notice a camera.
 *
 * DESIGN.md section 8: you learn where they are by driving past them, using the
 * same eyesight rule a plate is read by. A pole is a bigger thing than a plate
 * and it does not move, but you still have to be on the road and looking, so
 * this sits in the same order of magnitude as READ_ALONG_ROAD_METERS rather
 * than being generous about it.
 *
 * The asymmetry that makes this interesting is not the distance, it is the
 * memory: a plate fades in three minutes and a camera never fades at all,
 * because a camera cannot drive away. Going back to look again is not a feat of
 * memory, it is a fact about a pole.
 */
export const SPOT_CAMERA_METERS = 130;

/**
 * How close you have to be to put something over a lens.
 *
 * Obstructing a camera is a physical act and no message can perform one, which
 * is the same rule a traffic stop is decided by.
 *
 * Thirty meters rather than arm's length, because there is no walking in this
 * game and a car cannot park at the foot of a pole. This is the distance at
 * which somebody has plainly stopped *at* the camera rather than driven past
 * it, which is the thing the rule is actually for. It was twelve, which is
 * closer than a car can reliably get to a roadside pole set back from a
 * junction, so the act was effectively impossible.
 */
export const OBSTRUCT_REACH_METERS = 30;

/**
 * How long an obstruction lasts, in hours of world time.
 *
 * DESIGN.md section 8's cheapest tier: somebody walks over and removes it, and
 * that somebody can be anybody, including a passing citizen. So it is hours
 * rather than days, and it is a spread rather than a constant, because what
 * ends an obstruction is somebody noticing and somebody being sent and neither
 * of those runs on a timer.
 *
 * These are starting numbers. Section 8 is explicit that if crowbars start
 * outpacing procurement then the numbers are wrong rather than the mechanic.
 */
export const OBSTRUCT_MIN_HOURS = 3;
export const OBSTRUCT_MAX_HOURS = 30;

/**
 * How much more a road costs when the traveller would rather not be seen on it.
 *
 * A penalty rather than a ban, for two reasons that both matter.
 *
 * A hard ban would make some destinations unreachable, because a watched
 * approach is sometimes the only way in, and a game where avoiding cameras can
 * strand you is a game where nobody avoids cameras.
 *
 * Worse, a ban would leak. A client whose route suddenly fails has learned that
 * every path to the destination is watched, which is information about coverage
 * it did not earn by driving. A penalty never fails, so it never tells you
 * anything you did not already know.
 *
 * Twelve is set so a detour of several minutes beats a watched approach while a
 * road you have no real choice about is still taken.
 */
export const AVOID_EDGE_PENALTY = 12;

/**
 * The most anybody may exceed the posted limit by, with their foot down.
 *
 * Twice, and available rather than compulsory. **Anybody**: this was gated to
 * the administrator on the reasoning that it was a tool for driving across the
 * county without doing it at 45, which is a true thing about how it gets used
 * and completely wrong about what it is. It is the speeding mechanic. A game
 * where the civilian side cannot choose to break a traffic law has removed the
 * only ordinary decision it had.
 *
 * It costs something, which is why it is a toggle and not a default. A car
 * doing eighty in a forty is the most conspicuous object on the road: every
 * camera it passes reads it going that fast and the speed is in the record
 * forever, and any marked unit within PASS_WATCH_METERS pulls it over on the
 * spot. Those two consequences are the same offence and they are nothing alike,
 * which is the entire argument of the game in one button.
 *
 * On the enforcement side it also means lights and siren, because a marked car
 * doing double the limit with nothing on is just speeding.
 */
export const FAST_SPEED_MULTIPLIER = 2;

/**
 * How hard a unit runs during a pursuit.
 *
 * Above FAST_SPEED_MULTIPLIER, and that ordering is the mechanic: a runner
 * flat out at 2x is losing ground slowly rather than holding it, so a chase
 * ends -- with a stop, or with the head start and the call timer deciding it.
 * At 1.6 the first version of this could never close on anybody, which made
 * every pursuit a light show receding in the mirror.
 */
export const PURSUIT_SPEED_MULTIPLIER = 2.2;

/**
 * A pursuit is only a pursuit while the officer can see the car. These are the
 * two clocks of losing it, in world-time seconds (the sim runs a little over
 * twice real-time, so these are a few real seconds and a bit over a real minute).
 *
 * COAST: how long after sight breaks the cruiser keeps closing on the last known
 * position -- long enough to round a bend the car just took, not long enough to
 * chase a ghost. When it runs out the chase goes to 'lost' and the wheel is the
 * officer's: hold the road and peek the side streets, or gamble on a turn, and
 * watch the camera reads.
 *
 * GIVEUP: how long a 'lost' search runs with no eyes-on and no fresh camera read
 * before the plate is gone for good and the pursuit ends.
 */
export const PURSUIT_COAST_SECONDS = 9;
export const PURSUIT_LOST_GIVEUP_SECONDS = 150;

/**
 * What you can do to a camera, and what it costs to put right.
 *
 * DESIGN.md section 8's table, and the gradient is the whole design. If
 * reducing the network is the winning play then the game is about reducing the
 * network and the informational game dies -- which is Watch Dogs arrived at
 * from the other direction. So the point is not to forbid interference, it is
 * to get the arithmetic right:
 *
 * | tier | what it takes to fix | comes back where |
 * |---|---|---|
 * | obstruct | somebody walks over and removes it | same pole, hours |
 * | damage | a service call | same pole, a day or two |
 * | take | a replacement, and somebody works out it was stolen | somewhere new |
 * | destroy | a replacement | somewhere new |
 *
 * Obstruction is cheap because it is reversible and the fix is trivial.
 * Destruction is expensive because the fix is a procurement decision and the
 * replacement lands where somebody chose *after* losing the old one, so
 * **destroying a camera costs you the map you spent hours building**. The game
 * says obstruction is the clever play and destruction the desperate one by
 * arithmetic rather than by scolding.
 *
 * These are hours of world time, and world time runs at wall-clock rate, so
 * "several days" is several real days. Section 8 is explicit that if crowbars
 * start outpacing procurement then these numbers are wrong rather than the
 * mechanic.
 */
export const INTERFERENCE_TIERS = ['obstruct', 'damage', 'take', 'destroy'] as const;
export type Interference = (typeof INTERFERENCE_TIERS)[number];

export const REPAIR_HOURS: Readonly<Record<Interference, [number, number]>> = Object.freeze({
  // Section 8's own words for each tier: "hours", "a day or two", "several
  // days". The ranges are deliberately disjoint -- they overlapped at first,
  // which meant a lucky service call could beat an unlucky bag and the gradient
  // the whole design turns on stopped being legible from play.
  obstruct: [2, 18],
  damage: [20, 60],
  take: [70, 190],
  destroy: [70, 190],
});

/**
 * What a jurisdiction pays to put a downed camera back, by tier, out of the
 * enforcement revenue the network is supposed to be earning it.
 *
 * Section 8's line -- vandalism is a rounding error against procurement -- is
 * true right up until a department's poles keep getting hit, and then it watches
 * its revenue go straight back out the door. A covered lens is a truck-roll; a
 * reader taken or destroyed is a full replacement. This is the money-sink the
 * authority side otherwise lacks: revenue is the score, and repairs are the bill
 * against it. Charged only to police and sheriff owners -- a vendor's camera is
 * a vendor's problem, not the county's.
 */
export const REPAIR_COST: Readonly<Record<Interference, number>> = Object.freeze({
  obstruct: 200, // a service call to uncover or clean a lens
  damage: 900, // parts and labor on a damaged unit
  take: 2500, // a stolen reader, replaced
  destroy: 2500, // a destroyed reader, replaced
});

/**
 * How a lens actually gets covered.
 *
 * Obstruction was one undifferentiated tier, which made the cheapest and most
 * common thing anybody does to one of these the least specific thing in the
 * game. It is also the tier with real documented tactics attached: people have
 * tied **balloons** to them, put a bag over them, taped the lens, sprayed them.
 * Those are not the same act and they should not repair at the same rate.
 *
 * The multiplier is against REPAIR_HOURS.obstruct, and the ordering is the
 * obvious one: something tied on comes off in a moment, something stuck to the
 * glass takes longer, something sprayed on has to be cleaned.
 *
 * Note where this stops. Paint is the slowest *cover*, and it is still a cover
 * -- the moment a tool comes out it is `damage`, which is a different tier with
 * a different reach and a different repair time. The gradient §8 turns on is
 * between covering and breaking, and adding means to one end must not blur it.
 */
export const OBSTRUCTION_MEANS = ['balloons', 'bag', 'tape', 'paint'] as const;
export type Obstruction = (typeof OBSTRUCTION_MEANS)[number];

export const OBSTRUCTION_REPAIR: Readonly<Record<Obstruction, number>> = Object.freeze({
  balloons: 0.5,
  bag: 0.8,
  tape: 1.1,
  paint: 1.5,
});

/** What each one is called on a button, in the words somebody would use. */
export const OBSTRUCTION_WORDS: Readonly<Record<Obstruction, string>> = Object.freeze({
  balloons: 'balloons',
  bag: 'a bag',
  tape: 'tape',
  paint: 'paint',
});

/** Whether this tier brings the camera back somewhere else entirely. */
export function movesTheCamera(tier: Interference): boolean {
  return tier === 'take' || tier === 'destroy';
}

/**
 * How close you have to be, by tier.
 *
 * Covering a lens is done from a car window. Unbolting one is not, and the
 * distance is a stand-in for the time it takes -- there is no walking in this
 * game, so proximity is the only currency available for "this took a while".
 */
export const INTERFERENCE_REACH: Readonly<Record<Interference, number>> = Object.freeze({
  obstruct: 30,
  damage: 20,
  take: 12,
  destroy: 12,
});

/**
 * How close counts as arrived, for handing in a job.
 *
 * A junction is a point and a car stops near one rather than on it, so a strict
 * test would leave people idling beside a drop that refuses to accept them.
 */
export const ARRIVAL_METERS = 60;

/**
 * How long a camera contract runs, in days.
 *
 * DESIGN.md section 8: the contract is what moves a deployment wholesale, and
 * the interference tiers were bounded against it from the day they were
 * written -- vandalism is a rounding error against procurement, and if crowbars
 * outpace contracts then the game is base destruction wearing a different hat.
 *
 * Ninety days rather than a real annual term. A year of world time is a year of
 * wall clock in a persistent county, which means no player would ever see a
 * renewal, and a mechanic nobody sees is not one. A quarter still makes
 * procurement the slower hand -- a crowbar acts in hours -- while keeping it the
 * bigger one, because a review moves a whole department's allocation at once
 * and a crowbar moves one camera.
 */
export const CONTRACT_DAYS = 90;

/**
 * How hard a car may corner, in meters per second squared.
 *
 * Cars slow to turn. That is physics rather than a design choice, and the game
 * needs it for a reason beyond looking right: **the speed a car is doing when
 * it passes a camera decides both how likely the read is wrong and how likely
 * the driver notices the camera** -- and cameras sit on junction approaches, at
 * a measured median setback of 44 m, which is exactly where a turning car is
 * slowing down.
 *
 * Without cornering, every car takes every corner at the posted limit and the
 * junction is the fastest part of the trip, which is backwards.
 *
 * About 0.35g. Comfortable rather than maximum: this is somebody driving to the
 * shops, not a racing line.
 */
export const LATERAL_ACCEL = 3.4;

/**
 * How hard a car accelerates and brakes, in meters per second squared.
 *
 * Braking is stronger than acceleration, as it is in any car. These are gentle
 * figures for the same reason as above.
 */
export const ACCELERATION = 1.6;
export const BRAKING = 3.0;

/**
 * The slowest a corner ever forces anybody to go.
 *
 * A hairpin should not bring traffic to a stop, and a floor keeps a
 * pathological piece of OpenStreetMap geometry from parking a car forever.
 */
export const MIN_CORNER_SPEED = 3.0;

/**
 * Speed at or below which a turn costs nothing.
 *
 * About twenty miles an hour, which is the figure worth naming: below it, a car
 * takes an ordinary junction without slowing at all.
 */
export const FREE_CORNER_SPEED = 9.0;

/**
 * How wide a junction is, for working out a corner radius.
 *
 * The graph has no junction geometry -- an edge is a straight line between two
 * nodes -- so the radius a turn is taken on has to be inferred rather than
 * measured. Eight meters is a normal intersection.
 */
export const JUNCTION_RADIUS_METERS = 8;

/**
 * Stopped near a camera, and looking at it.
 *
 * A pass at a camera normally resolves when the camera leaves sight, and that
 * is right for driving past one. It is wrong for somebody who parks next to
 * one: they never leave, so the pass never resolves, and the single most
 * reliable way to find a camera would be the one way that never worked at all.
 *
 * So standing still near one is a look, resolved on a beat and started again.
 * That makes deliberate searching work -- and it costs exactly what it should,
 * because sitting at a junction repeatedly is also the most conspicuous thing
 * anybody can do in front of a plate reader.
 */
export const LOOKING_SPEED = 2.0;
export const LOOKING_SECONDS = 4;

/**
 * What distance costs, on top of the time it takes, in seconds per meter.
 *
 * A router that minimises time alone will take any detour that saves a second,
 * and the result is that every trip becomes a motorway trip. Measured on
 * Muskego to Hartland: the time-optimal route drove **7.1 km further to save
 * 2.1 minutes**, and got there by heading 2.8 km *east* up Moorland Road to
 * reach an on-ramp before turning west. Nobody drives an errand like that.
 *
 * People trade time against distance rather than minimising one of them. Fuel,
 * wear and attention all scale with how far you go, and a twenty per cent
 * longer drive to save seven per cent of the clock is a trade almost nobody
 * takes. This is the term that says so. It is a tunable rather than a measured
 * figure, and it is set where that particular detour stops being worth it.
 *
 * It matters beyond realism. The interesting part of this county -- the
 * cameras, the towns, the coverage holes -- is on the surface streets. A router
 * that puts every journey on the interstate hides the game.
 */
export const DISTANCE_COST_SECONDS_PER_METER = 0.02;

/**
 * A shift on the console, and the rest between them.
 *
 * In world hours. Switching sides needed a gate, and the obvious ones are a
 * price or a cooldown -- but this game already has the right vocabulary for it,
 * because **policing is a job**. You sign on, you work a shift, it ends, you go
 * back to driving. Taking another means signing on again.
 *
 * That gets three things a toggle does not:
 *
 * - It is a commitment with a length, so being on the console is a decision
 *   about the next hour rather than a click you undo when a query is awkward.
 * - The cost is the right one: opportunity. Nobody on shift is taking civilian
 *   work, and nobody driving is running searches.
 * - It fits what the thing is. An officer is somebody doing a job for a
 *   department, which is exactly what the agency posting already says.
 *
 * Ending early is allowed and still costs the rest period, because a shift you
 * abandoned is still a shift somebody covered for you.
 */
export const SHIFT_HOURS = 4;
export const REST_HOURS = 4;

/**
 * What a stop is worth to the department that made it.
 *
 * Enforcement generated nothing. Cases closed justified the contract and that
 * was the whole of it, which left the money side of these programmes out of a
 * game whose subject is partly the money side of these programmes.
 *
 * A citation is a fine, and the fine is **somebody's money** -- it comes out of
 * the account of the person stopped and goes to the department that stopped
 * them. That is the loop closing honestly: the revenue is not conjured, it is
 * transferred, and a player who has been stopped four times over an expired tag
 * can see exactly where their week's earnings went.
 *
 * A recovery is worth more and costs nobody, because the vehicle was stolen and
 * returning it is the thing the programme was sold on.
 */
export const CITATION_FINE = 95;
export const RECOVERY_VALUE = 300;

/**
 * Fuel.
 *
 * A tank is a leash. It is the one thing that makes *where you are* constrain
 * *where you can go*, and in a game about a network that watches the roads it
 * does something no other mechanic does: **it makes you stop somewhere real, on
 * a schedule, in public.**
 *
 * DESIGN.md section 8 is explicit that routine is the substrate -- *they fuel up
 * when the tank gets low, which is a schedule whether they think of it as one or
 * not* -- and that the quietly alarming thing in one of these datasets is not a
 * crime, it is the pattern. Fuel is that pattern, generated rather than scripted:
 * the county has 78 real filling stations and a driver ends up using the two or
 * three near the roads they actually drive.
 *
 * Range is set so a tank covers a good few errands but not a week of them.
 * Waukesha is about 25 miles across, so this is a handful of crossings.
 */
/**
 * A tank, in gallons.
 *
 * American units throughout, because this is an American county and the
 * numbers are read by somebody who thinks in them. The road graph stays in
 * meters -- UTM is meters and OpenStreetMap posts limits in km/h, so those
 * come from the source data and converting them would only add a place to be
 * wrong -- but fuel is the game's own invention and there is no reason for it
 * to be in liters.
 */
export const TANK_GALLONS = 14.5;
/** Miles per gallon, which is a fairly ordinary car. */
export const FUEL_MPG = 25;
/** What it costs to fill, per gallon. */
export const FUEL_PRICE_PER_GALLON = 3.6;
/** Below this fraction, the car is worth worrying about. */
export const FUEL_LOW = 0.2;
/** How close you have to be to a pump. About two hundred feet. */
export const REFUEL_METERS = 60;
